import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "../services/apiClient";
import { useAuth } from "../App";
import BackNavigationButton from "../components/navigation/BackNavigationButton";
import { useFormDraft } from "../hooks/useFormDraft";
import { useUnsavedChangesGuard } from "../hooks/useUnsavedChangesGuard";

const createInitialFormData = () => ({
  name: "",
  description: "",
  specialties: "",
  years_experience: "",
  license_number: "",
  phone: "",
  website: "",
  image_url: "",
  certifications: "",
  service_areas: "",
});

const BrokerProfileEdit = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(createInitialFormData());
  const [initialSnapshot, setInitialSnapshot] = useState("");
  const draftKey = useMemo(
    () => `draft:broker-profile-edit:${user?.id || user?.email || "anon"}`,
    [user],
  );
  const isDirty = useMemo(() => {
    if (!initialSnapshot) return false;
    return JSON.stringify(formData) !== initialSnapshot;
  }, [formData, initialSnapshot]);
  const { clearDraft } = useFormDraft({
    draftKey,
    formData,
    setFormData,
    enabled: Boolean(user),
  });
  const { confirmIfDirty } = useUnsavedChangesGuard(isDirty);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get("/brokers/me/profile");
      if (response.ok) {
        const data = await response.json();
        const mappedFormData = {
          name: data.name || "",
          description: data.description || "",
          specialties: data.specialties?.join(", ") || "",
          years_experience: data.years_experience || "",
          license_number: data.license_number || "",
          phone: data.phone || "",
          website: data.website || "",
          image_url: data.image_url || "",
          certifications: data.certifications?.join(", ") || "",
          service_areas: data.service_areas?.join(", ") || "",
        };
        setFormData(mappedFormData);
        setInitialSnapshot(JSON.stringify(mappedFormData));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const profileData = {
        name: formData.name,
        description: formData.description,
        specialties: formData.specialties
          ? formData.specialties.split(",").map((s) => s.trim())
          : [],
        years_experience: formData.years_experience
          ? parseInt(formData.years_experience)
          : null,
        license_number: formData.license_number || null,
        phone: formData.phone || null,
        website: formData.website || null,
        image_url: formData.image_url || null,
        certifications: formData.certifications
          ? formData.certifications.split(",").map((c) => c.trim())
          : [],
        service_areas: formData.service_areas
          ? formData.service_areas.split(",").map((a) => a.trim())
          : [],
      };

      await api.put("/brokers/me/profile", profileData);

      toast.success("Perfil actualizado");
      clearDraft();
      navigate("/dashboard");
    } catch (error) {
      console.error("Error updating broker profile:", error);
      toast.error("Error al actualizar perfil");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (!confirmIfDirty()) return;
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-muted">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="mb-4">
            <BackNavigationButton
              onBeforeNavigate={confirmIfDirty}
              dataTestId="broker-profile-edit-back"
            />
          </div>
          <h1
            className="text-4xl font-bold mb-4"
            style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
          >
            Editar Mi Perfil de Broker
          </h1>
          <p className="text-muted-foreground">
            Completa tu perfil para aumentar tu credibilidad y atraer más
            clientes
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-background rounded-lg p-8 space-y-8"
        >
          {/* Información Básica */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold pb-2 border-b">
              Información Básica
            </h2>

            <div>
              <label className="text-sm font-medium mb-2 block">
                Nombre Completo o Razón Social *
              </label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Tu nombre o nombre de tu agencia"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">
                Descripción Profesional *
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Describe tu experiencia, enfoque y cómo ayudas a tus clientes..."
                rows={5}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Esta descripción aparece en tu perfil público
              </p>
            </div>
          </div>

          {/* Credenciales */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold pb-2 border-b">
              Credenciales Profesionales
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Número de Licencia
                </label>
                <Input
                  value={formData.license_number}
                  onChange={(e) =>
                    setFormData({ ...formData, license_number: e.target.value })
                  }
                  placeholder="Ej: BRK-2024-001"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Años de Experiencia
                </label>
                <Input
                  type="number"
                  value={formData.years_experience}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      years_experience: e.target.value,
                    })
                  }
                  placeholder="Ej: 5"
                  min="0"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">
                Especialidades (separadas por comas)
              </label>
              <Textarea
                value={formData.specialties}
                onChange={(e) =>
                  setFormData({ ...formData, specialties: e.target.value })
                }
                placeholder="Ej: Residencial, Comercial, Preventa, Propiedades de Lujo"
                rows={2}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Aparecen como badges en tu perfil
              </p>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">
                Certificaciones (separadas por comas)
              </label>
              <Textarea
                value={formData.certifications}
                onChange={(e) =>
                  setFormData({ ...formData, certifications: e.target.value })
                }
                placeholder="Ej: AMPI, NAR, Certificación en Inversiones Inmobiliarias"
                rows={2}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Aumentan tu credibilidad profesional
              </p>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">
                Zonas de Cobertura (separadas por comas)
              </label>
              <Textarea
                value={formData.service_areas}
                onChange={(e) =>
                  setFormData({ ...formData, service_areas: e.target.value })
                }
                placeholder="Ej: San Pedro Garza García, Monterrey Centro, Santa Catarina, Valle Oriente"
                rows={2}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Indica las zonas donde ofreces tus servicios
              </p>
            </div>
          </div>

          {/* Contacto */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold pb-2 border-b">
              Información de Contacto
            </h2>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                🔒 <strong>Privacidad Total:</strong> Tu teléfono, email y sitio
                web NO se muestran públicamente. Solo el equipo de Metal
                Concrete puede verlos. Los clientes te contactarán a través del
                formulario de tu perfil.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Teléfono
                </label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  placeholder="+52 811 555 1234"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Solo visible para administradores
                </p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Sitio Web
                </label>
                <Input
                  type="text"
                  value={formData.website}
                  onChange={(e) =>
                    setFormData({ ...formData, website: e.target.value })
                  }
                  placeholder="https://tuagencia.com o @tuusuario"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Opcional · Puedes ingresar un URL o red social
                </p>
              </div>
            </div>
          </div>

          {/* Imagen */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold pb-2 border-b">
              Imagen de Perfil
            </h2>

            <div>
              <label className="text-sm font-medium mb-2 block">
                URL de Foto de Perfil
              </label>
              <Input
                type="url"
                value={formData.image_url}
                onChange={(e) =>
                  setFormData({ ...formData, image_url: e.target.value })
                }
                placeholder="https://ejemplo.com/mi-foto.jpg"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Recomendado: Foto profesional, formato cuadrado, mínimo
                400x400px
              </p>
              {formData.image_url && (
                <div className="mt-4">
                  <p className="text-xs font-medium mb-2">Vista previa:</p>
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="w-32 h-32 rounded-full object-cover border-4 border-primary/20"
                    onError={(e) => {
                      e.target.style.display = "none";
                      toast.error("URL de imagen inválida");
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-4 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? "Guardando..." : "Guardar cambios"}
            </Button>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default BrokerProfileEdit;
